import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { keyframes } from "@emotion/react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { pagePrimaryButtonSx } from "../../styles/pageModernStyles";

const rise = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pop = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

const shutter = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(0.92); }
  40%, 70% { opacity: 1; transform: scale(1); }
`;

const draw = keyframes`
  0%, 8% { stroke-dashoffset: 180; }
  45%, 100% { stroke-dashoffset: 0; }
`;

const sheet = keyframes`
  0%, 15% { opacity: 0; transform: translateY(8px); }
  35%, 75% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(4px); }
`;

const tiles = keyframes`
  0%, 20% { opacity: 0; transform: scale(0.85); }
  40%, 80% { opacity: 1; transform: scale(1); }
  100% { opacity: 0.85; }
`;

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const JACKET =
  "M10 16 L16 8 H21 L24 13 L27 8 H32 L38 16 L44 24 L36 27 V50 H12 V27 L4 24 Z";

function Stage({ children, tint }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 86,
        overflow: "hidden",
        bgcolor: tint,
        lineHeight: 0,
        fontSize: 0,
      }}
    >
      {children}
    </Box>
  );
}

function DibujoPreview() {
  return (
    <Stage tint="#eef5ff">
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: 14,
          width: 54,
          height: 58,
          transform: "translateX(-50%)",
          borderRadius: 1.5,
          bgcolor: "#fff",
          border: "1px solid",
          borderColor: alpha("#1473e6", 0.28),
          boxShadow: `0 6px 16px ${alpha("#1473e6", 0.12)}`,
          overflow: "hidden",
          animation: `${shutter} 3.6s ease-in-out infinite`,
          ...motionSafe,
          "&:before": {
            content: '""',
            display: "block",
            height: 28,
            m: 0.75,
            borderRadius: 1,
            background: `linear-gradient(145deg, ${alpha("#1473e6", 0.55)}, ${alpha("#0ea5e9", 0.25)})`,
          },
          "&:after": {
            content: '""',
            display: "block",
            height: 5,
            mx: 1.1,
            mt: 0.55,
            borderRadius: 1,
            bgcolor: alpha("#1473e6", 0.28),
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: 18,
          bottom: 12,
          width: 18,
          height: 18,
          borderRadius: "50%",
          border: "2px solid #1473e6",
          bgcolor: alpha("#1473e6", 0.12),
          animation: `${pop} 2.4s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
    </Stage>
  );
}

function SketchPreview() {
  return (
    <Stage tint="#f3efe8">
      <Box
        component="svg"
        viewBox="0 0 48 56"
        sx={{
          position: "absolute",
          left: "50%",
          top: 10,
          width: 52,
          height: 64,
          transform: "translateX(-50%)",
          "& path": {
            animation: `${draw} 4.2s ease-in-out infinite`,
            ...motionSafe,
          },
        }}
      >
        <path
          d={JACKET}
          fill="none"
          stroke="#0f766e"
          strokeWidth="1.7"
          strokeLinejoin="round"
          strokeDasharray="180"
        />
      </Box>
    </Stage>
  );
}

function FichaPreview() {
  return (
    <Stage tint="#f3f0ff">
      {[0, 1].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: 22 + i * 18,
            top: 12 + i * 6,
            width: 48,
            height: 58,
            borderRadius: 1,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: alpha("#4338ca", 0.25),
            boxShadow: `0 4px 12px ${alpha("#4338ca", 0.1)}`,
            animation: `${sheet} 4.2s ease-in-out infinite`,
            animationDelay: `${i * 0.18}s`,
            ...motionSafe,
            "&:before": {
              content: '""',
              display: "block",
              height: 16,
              m: 0.7,
              borderRadius: 0.6,
              bgcolor: alpha("#4338ca", 0.35),
            },
            "&:after": {
              content: '""',
              display: "block",
              height: 4,
              mx: 0.9,
              mt: 0.5,
              borderRadius: 1,
              bgcolor: alpha("#4338ca", 0.2),
            },
          }}
        />
      ))}
    </Stage>
  );
}

function MoodPreview() {
  const colors = ["#1c1917", "#a16207", "#d6d3d1", "#0f766e"];
  return (
    <Stage tint="#f7f3ec">
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.75,
          px: 1.5,
        }}
      >
        {colors.map((color, i) => (
          <Box
            key={color}
            sx={{
              width: 22,
              height: 40,
              borderRadius: 1,
              bgcolor: color,
              animation: `${tiles} 3.8s ease-in-out infinite`,
              animationDelay: `${i * 0.12}s`,
              ...motionSafe,
            }}
          />
        ))}
      </Box>
    </Stage>
  );
}

const PREVIEWS = {
  dibujo: DibujoPreview,
  sketch: SketchPreview,
  ficha: FichaPreview,
  moodboard: MoodPreview,
};

const OPTIONS = [
  {
    id: "dibujo",
    familyKey: "generateMenu.familyDesign",
    titleKey: "generateMenu.dibujoTitle",
    blurbKey: "generateMenu.dibujoBlurb",
    accent: "#1473e6",
  },
  {
    id: "sketch",
    familyKey: "generateMenu.familyDesign",
    titleKey: "generateMenu.sketchTitle",
    blurbKey: "generateMenu.sketchBlurb",
    accent: "#0f766e",
  },
  {
    id: "ficha",
    familyKey: "generateMenu.familyCompass",
    titleKey: "generateMenu.fichaTitle",
    blurbKey: "generateMenu.fichaBlurb",
    accent: "#4338ca",
  },
  {
    id: "moodboard",
    familyKey: "generateMenu.familyCompass",
    titleKey: "generateMenu.moodTitle",
    blurbKey: "generateMenu.moodBlurb",
    accent: "#a16207",
  },
];

function OptionCard({
  option,
  selected,
  disabled,
  disabledReason,
  onSelect,
  index,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const Preview = PREVIEWS[option.id];
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      aria-pressed={selected}
      title={disabled ? disabledReason : undefined}
      onClick={() => !disabled && onSelect(option.id)}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        p: 0,
        m: 0,
        textAlign: "left",
        cursor: disabled ? "not-allowed" : "pointer",
        borderRadius: 2.5,
        overflow: "hidden",
        border: "2px solid",
        borderColor: selected ? option.accent : "divider",
        bgcolor: "background.paper",
        opacity: disabled ? 0.55 : 1,
        boxShadow: selected
          ? `0 8px 22px ${alpha(option.accent, 0.16)}`
          : "none",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        font: "inherit",
        color: "inherit",
        WebkitAppearance: "none",
        appearance: "none",
        animation: `${rise} 0.4s ease both`,
        animationDelay: `${index * 0.06}s`,
        ...motionSafe,
        "&:hover": disabled
          ? undefined
          : {
              borderColor: selected
                ? option.accent
                : alpha(option.accent, 0.45),
            },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box sx={{ position: "relative", lineHeight: 0, flexShrink: 0 }}>
        <Preview />
        {selected ? (
          <CheckCircleIcon
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              color: option.accent,
              bgcolor: "#fff",
              borderRadius: "50%",
              fontSize: 20,
            }}
          />
        ) : null}
      </Box>
      <Box sx={{ px: 1.35, py: 1.15, lineHeight: 1.35 }}>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontWeight: 800,
            letterSpacing: 0.06,
            textTransform: "uppercase",
            color: option.accent,
            mb: 0.35,
          }}
        >
          {t(`presentacionesSearch.newDesign.${option.familyKey}`)}
        </Typography>
        <Typography fontWeight={800} fontSize="0.95rem">
          {t(`presentacionesSearch.newDesign.${option.titleKey}`)}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.4, lineHeight: 1.4 }}
        >
          {t(`presentacionesSearch.newDesign.${option.blurbKey}`)}
        </Typography>
        {disabled && disabledReason ? (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.6,
              color: "warning.main",
              fontWeight: 600,
            }}
          >
            {disabledReason}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

/**
 * Dialog estilo Vectorton / Compás pick: Dibujo / Sketch / Ficha / Moodboard.
 */
export default function AiDesignsGenerarDialog({
  open,
  onClose,
  onChoose,
  dibujoReady,
  sketchReady,
  boardsReady,
  dibujoBlockedReason,
  sketchBlockedReason,
  boardsBlockedReason,
  busy,
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState("dibujo");

  useEffect(() => {
    if (!open) return;
    if (dibujoReady) setSelected("dibujo");
    else if (sketchReady) setSelected("sketch");
    else if (boardsReady) setSelected("ficha");
    else setSelected("dibujo");
  }, [open, dibujoReady, sketchReady, boardsReady]);

  const disabledFor = (id) => {
    if (id === "dibujo") return !dibujoReady;
    if (id === "sketch") return !sketchReady;
    if (id === "ficha" || id === "moodboard") return !boardsReady;
    return false;
  };

  const reasonFor = (id) => {
    if (id === "dibujo") return dibujoBlockedReason || "";
    if (id === "sketch") return sketchBlockedReason || "";
    if (id === "ficha" || id === "moodboard") return boardsBlockedReason || "";
    return "";
  };

  const canConfirm = selected && !disabledFor(selected) && !busy;

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose?.()}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          mx: 2,
          width: "100%",
          maxWidth: 560,
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={800}>
          {t("presentacionesSearch.newDesign.generateMenu.title", {
            defaultValue: "¿Qué quieres generar?",
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t("presentacionesSearch.newDesign.generateMenu.lead", {
            defaultValue:
              "Elige el camino. Sketch sale en PNG; luego Generar SVG debajo del preview. Ficha y moodboard usan las versiones del historial.",
          })}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1.25,
            pt: 1.5,
            width: "100%",
          }}
        >
          {OPTIONS.map((option, index) => (
            <OptionCard
              key={option.id}
              option={option}
              index={index}
              selected={selected === option.id}
              disabled={disabledFor(option.id)}
              disabledReason={reasonFor(option.id)}
              onSelect={setSelected}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          {t("presentacionesSearch.newDesign.generateMenu.cancel", {
            defaultValue: "Cancelar",
          })}
        </Button>
        <Button
          variant="contained"
          sx={pagePrimaryButtonSx}
          disabled={!canConfirm}
          onClick={() => onChoose?.(selected)}
        >
          {t("presentacionesSearch.newDesign.generateMenu.continue", {
            defaultValue: "Continuar",
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
